---
title: Glossary
summary: Precise definitions of the terms used across the product and these docs.
visibility: authenticated
audience: author-admin
section: Reference
order: 90
last_verified: 2026-07-27
owner: docs
---

# Glossary

**Persona** — A configurable expert that people talk to. It owns its identity, its
knowledge base, and its presentation mode (chat, voice, or avatar). You own the personas
you create; they can be private, shared with specific people, or public.

**RAG (Retrieval-Augmented Generation)** — Answering by first *retrieving* relevant
passages from a knowledge base, then *generating* a reply that uses them. See
[How RAG works here](/help/concepts/how-rag-works).

**Grounding** — Keeping a persona's answers tied to retrieved passages rather than the
model's general knowledge. Achieved by combining the system prompt, score threshold, top
K, graph, and temperature — not a single switch.

**Graph / Graph ID** — A knowledge graph is one partition of documents on the RAG server.
A persona's **Graph ID** (a short lowercase slug) selects which graph it retrieves from.

**Citation / Source** — A passage the persona retrieved and used. Citations appear beside
an answer and link to the underlying text.

**System prompt** — The persona's standing instructions: who it is, how it speaks, and how
strictly to stay grounded.

**Score threshold / Top K** — Retrieval controls. *Score threshold* is the minimum
relevance a passage needs to be used; *Top K* is how many passages are retrieved per
question.

**Presentation mode** — How a persona is experienced: **chat** (text), **voice**
(spoken), or **avatar** (video).

**Bundle** — *(advanced runtime)* A versioned recipe that defines what a persona is and
can do — its instruction blocks, skills, tool bindings, and workflow bindings. One bundle
version is active at a time.

**Skill** — *(advanced runtime)* A reusable pack of instructions for a kind of task (for
example, *place an order*). A skill declares the capabilities it needs but does not wire
them up.

**Capability** — *(advanced runtime)* The vendor-neutral contract for an action a persona
can take, such as "create a payment link." Capabilities are platform-defined.

**Adapter** — *(advanced runtime)* A concrete implementation that fulfills one or more
capabilities (in-process, an MCP server, or an HTTP service).

**Tool binding** — *(advanced runtime)* The wiring on a bundle that connects a capability
to an adapter and, when needed, to a credential set. ("Tool" is the everyday word for a
capability wired up this way.)

**Credential set** — A named, encrypted group of API keys, managed by an administrator. A
tool binding references a credential set when its action needs credentials.

**Workflow** — *(advanced runtime)* A guided, multi-step flow built as a graph of
**nodes** (talk to the user, call a tool, branch, wait for an event, finish).

**Slot** — *(advanced runtime)* A typed value a workflow collects as it runs (for example,
a customer's phone number), referenced by later steps.

**Guardrail** — *(advanced runtime)* A plain-language rule placed on a workflow step where
the persona talks, constraining what it may say or do at that step.

**Feature / feature profile** — A *feature* is a capability toggled on or off per
deployment (for example, *voice* or *whatsapp*). A *profile* (such as `full`,
`smb`, `knowledge-ops`, `edge-local`, or `minimal`) is a preset bundle of features.
What you see in the app depends on the deployment's enabled features.
